import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { LabelInput } from '@/components/ui/inputs';
import { PrimaryButton } from '@/components/ui/buttons';
import { Text } from '@/components/ui/text';
import { useLogin } from '@/hooks/account/useLogin.ts';
import { colors } from '@/assets/styles/colors';
import { IconScout } from '@/components/ui/images/IconScout';
import { LocalIcon } from '@/components/ui/images/LocalIcon';
import { getRexSSOLoginUrl } from '@/api/core/apiConfig';


interface LoginFormProps {
  onForgotPassword: () => void;
  onNeedHelp: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  onForgotPassword,
  onNeedHelp,
}) => {
  const { t: translate } = useTranslation();
  const {
    register,
    handleSubmit,
    errors,
    isSubmitting,
  } = useLogin();

  const handleSSOLogin = useCallback(() => {
    window.location.href = getRexSSOLoginUrl();
  }, []);

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <LabelInput
        id="username"
        label="Nombre de usuario"
        color={errors.username ? 'error' : 'important'}
        supportingText={errors.username?.message}
        required
        registration={register('username')}
        iconRight={<IconScout name="user" size="md" />}
        variant="floating"
      />

      <LabelInput
        id="password"
        type="password"
        label="Contraseña"
        color={errors.password ? 'error' : 'important'}
        supportingText={errors.password?.message}
        required
        registration={register('password')}
        variant="floating"
      />

      <div className="pt-2">
        <PrimaryButton
          type="submit"
          colorVariant="success"
          isLoading={isSubmitting}
          fullWidth
        >
          Iniciar sesión
        </PrimaryButton>
      </div>

      <div className="flex justify-between items-center text-sm pt-4 mt-6">
        <Text
          variant="body-medium"
          onClick={onForgotPassword}
          className="cursor-pointer transition-colors hover:opacity-80"
          color= {colors.primary.dash}
        >
          Olvidé mi contraseña
        </Text>
        <Text
          variant="subheader-medium"
          onClick={onNeedHelp}
          className="cursor-pointer transition-colors hover:opacity-80"
          color={colors.grays.neutral99}
        >
          ¿Necesitas ayuda?
        </Text>
      </div>

      <div>
        <div className="flex items-center gap-3">
          <div className="flex-1 h-px" style={{ backgroundColor: colors.grays.neutralCC }} />
          <Text variant="subheader-regular" color={colors.grays.neutral66}>
            {translate('auth:login.ssoSeparator')}
          </Text>
          <div className="flex-1 h-px" style={{ backgroundColor: colors.grays.neutralCC }} />
        </div>
        <div className="flex justify-center items-center gap-2 mt-3">
          <LocalIcon name="logoRex" className="w-6 h-6 rounded-[7px] object-contain" />
          <Text
            variant="subheader-medium"
            color={colors.important.main}
            onClick={handleSSOLogin}
            className="cursor-pointer hover:opacity-80 transition-opacity"
          >
            {translate('auth:login.ssoLink')}
          </Text>
        </div>
      </div>
    </form>
  );
};
